import React, { useEffect, useState } from "react";
import { fmtINR, api } from "@/lib/api";
import { Bank, ArrowDownRight, ArrowUpRight, Pulse } from "@phosphor-icons/react";

const STATUS_META = {
  healthy: {
    label: "HEALTHY",
    tile: "cash-healthy-tile",
    dot: "bg-emerald-300",
    note: "Net comfortably positive",
    valueClass: "cash-status-healthy",
    badgeClass: "border-emerald-300/60",
  },
  tight: {
    label: "TIGHT",
    tile: "cash-tight-tile",
    dot: "bg-amber-200",
    note: "Margin is thin",
    valueClass: "cash-status-tight",
    badgeClass: "border-amber-300/60",
  },
  shortfall: {
    label: "SHORTFALL",
    tile: "cash-shortfall-tile cash-blink",
    dot: "bg-red-300",
    note: "Cash arrangement needed",
    valueClass: "cash-status-shortfall cash-blink",
    badgeClass: "border-red-300/70 cash-blink",
  },
};

export default function KpiStrip({ kpi, refreshKey }) {
  const status = kpi?.status || "healthy";
  const meta = STATUS_META[status];
  const [accountCount, setAccountCount] = useState(0);

  useEffect(() => {
    api.get("/accounts").then((r) => setAccountCount((r.data || []).length)).catch(() => {});
  }, [refreshKey]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 relative z-20" data-testid="kpi-strip">
      <Tile
        testid="kpi-bank-balance"
        gradient="kpi-tile-indigo"
        icon={<Bank size={18} weight="duotone" />}
        label="Bank balance"
        value={fmtINR(kpi?.bank_balance)}
        sub={accountCount > 0 ? `Across ${accountCount} account${accountCount === 1 ? "" : "s"} · see Cash Health` : "Upload bank statements to see accounts"}
      />
      <Tile
        testid="kpi-projected-inflow"
        gradient="kpi-tile-emerald"
        icon={<ArrowUpRight size={18} weight="bold" />}
        label="Projected inflow"
        value={fmtINR(kpi?.projected_inflow)}
        sub={`Swiggy · Zomato · PetPooja · Other income · ${kpi?.horizon_days ?? 7}d`}
      />
      <Tile
        testid="kpi-committed-outflow"
        gradient="kpi-tile-rose"
        icon={<ArrowDownRight size={18} weight="bold" />}
        label="Committed outflow"
        value={fmtINR(kpi?.committed_outflow)}
        sub={`Predicted vendors · recurring expenses · ${kpi?.horizon_days ?? 7}d`}
        popover={kpi?.breakdown?.committed_outflow_windows && (
          <OutflowWindowPopover w={kpi.breakdown.committed_outflow_windows} />
        )}
      />
      <Tile
        testid="kpi-net-position"
        gradient={meta.tile}
        icon={<Pulse size={18} weight="duotone" />}
        label="Net position"
        value={fmtINR(kpi?.net_position)}
        sub={meta.note}
        badge={
          <div className={`inline-flex items-center gap-2 px-2 py-1 bg-white/15 backdrop-blur-sm text-white border ${meta.badgeClass} text-[10px] uppercase tracking-[0.2em] font-bold rounded-sm`} data-testid="kpi-status-badge">
            <span className={`h-1.5 w-1.5 rounded-full ${meta.dot} pulse-dot`} />
            {meta.label}
          </div>
        }
      />
    </div>
  );
}

function Tile({ icon, label, value, sub, gradient, badge, testid, valueClass, popover }) {
  return (
    <div className={`${gradient} text-white rounded-md p-5 relative ${popover ? "group" : "overflow-hidden"} shadow-sm`} data-testid={testid}>
      <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full bg-white/10 pointer-events-none" />
      <div className="absolute -bottom-12 -left-8 w-28 h-28 rounded-full bg-black/10 pointer-events-none" />
      <div className="relative flex items-center justify-between text-white/85">
        <div className="text-[10px] uppercase tracking-[0.2em] font-bold">{label}</div>
        <div className="h-7 w-7 grid place-items-center rounded-sm bg-white/15 backdrop-blur-sm">{icon}</div>
      </div>
      <div className={`relative font-mono text-2xl md:text-[26px] mt-3 tabular-nums leading-none kpi-value ${valueClass || ""}`}>{value}</div>
      <div className="relative flex items-end justify-between mt-3 gap-3">
        <div className="text-[11px] text-white/75 leading-tight">{sub}</div>
        {badge}
      </div>
      {popover}
    </div>
  );
}

function OutflowWindowPopover({ w }) {
  if (!w) return null;
  const days = w.month_days || 30;
  return (
    <div
      className="absolute left-1/2 -translate-x-1/2 top-full mt-2 z-50 hidden group-hover:block w-[420px] max-h-[480px] overflow-y-auto rounded-md shadow-2xl p-3"
      data-testid="kpi-committed-outflow-popover"
      style={{ background: "#ffffff", border: "1px solid #e2e8f0", color: "#0f172a" }}
    >
      <div className="text-[10px] uppercase tracking-wider font-bold mb-2" style={{ color: "#94a3b8" }}>
        Committed outflow horizon
      </div>

      <WindowSection title="Next 7 days"        total={w.d7}        items={w.items_d7 || []}    predItems={w.pred_items_d7 || []} />
      <WindowSection title="Next 15 days"       total={w.d15}       items={w.items_d15 || []}   predItems={w.pred_items_d15 || []} />
      <WindowSection title="Next 22 days"       total={w.d22}       items={w.items_d22 || []}   predItems={w.pred_items_d22 || []} />
      <WindowSection title={`Next ${days} days`} total={w.month_end} items={w.items_month || []} predItems={w.pred_items_month || []} />

      <div className="mt-2 pt-2 flex items-center justify-between text-[11px]" style={{ borderTop: "1px solid #f1f5f9" }}>
        <span className="font-semibold uppercase tracking-wider text-[9px]" style={{ color: "#64748b" }}>
          By {new Date(w.month_end_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}
        </span>
        <span className="font-mono tabular-nums font-bold" style={{ color: "#0f172a" }}>{fmtINR(w.month_end)}</span>
      </div>

      <div
        className="absolute left-1/2 -translate-x-1/2 bottom-full w-2 h-2 rotate-45"
        style={{ marginBottom: "-1px", background: "#ffffff", borderLeft: "1px solid #e2e8f0", borderTop: "1px solid #e2e8f0" }}
      />
    </div>
  );
}

function WindowSection({ title, total, items, predItems = [] }) {
  return (
    <div className="group/win mt-1.5 pb-1.5" style={{ borderBottom: "1px solid #f1f5f9" }}>
      <div className="flex items-center justify-between text-xs cursor-default">
        <span className="font-bold" style={{ color: "#0f172a" }}>{title}</span>
        <span className="font-mono tabular-nums font-bold" style={{ color: "#0f172a" }}>{fmtINR(total)}</span>
      </div>
      {/* Line items hidden until this specific section is hovered */}
      <div className="hidden group-hover/win:block">
        {predItems && predItems.length > 0 && (
          <>
            <div className="mt-1.5 text-[9px] uppercase tracking-wider font-bold" style={{ color: "#0891b2" }}>Predicted vendor payments</div>
            <table className="w-full mt-0.5 text-[10px]" style={{ color: "#475569" }}>
              <tbody>
                {predItems.map((it, i) => (
                  <tr key={`p-${i}`}>
                    <td className="py-0.5 pr-2 truncate max-w-[200px]" title={it.vendor}>
                      <span style={{ color: "#0f172a", fontWeight: 600 }}>{it.vendor || "—"}</span>
                      {it.cadence && <span style={{ color: "#94a3b8" }}> · {it.cadence}</span>}
                    </td>
                    <td className="py-0.5 pr-2 font-mono tabular-nums whitespace-nowrap" style={{ color: "#64748b" }}>
                      {it.due_date ? new Date(it.due_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }) : "—"}
                    </td>
                    <td className="py-0.5 text-right font-mono tabular-nums whitespace-nowrap" style={{ color: "#0f172a" }}>
                      {fmtINR(it.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
        {items && items.length > 0 ? (
          <>
            {predItems && predItems.length > 0 && (
              <div className="mt-1.5 text-[9px] uppercase tracking-wider font-bold" style={{ color: "#64748b" }}>Recurring expenses</div>
            )}
            <table className="w-full mt-0.5 text-[10px]" style={{ color: "#475569" }}>
              <tbody>
                {items.map((it, i) => (
                  <tr key={i}>
                    <td className="py-0.5 pr-2 truncate max-w-[200px]" title={`${it.task || ""} · ${it.location || ""}`}>
                      <span style={{ color: "#0f172a", fontWeight: 600 }}>{it.vendor || it.task || "—"}</span>
                      {it.location && <span style={{ color: "#94a3b8" }}> · {it.location}</span>}
                    </td>
                    <td className="py-0.5 pr-2 font-mono tabular-nums whitespace-nowrap" style={{ color: "#64748b" }}>
                      {new Date(it.due_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}
                    </td>
                    <td className="py-0.5 text-right font-mono tabular-nums whitespace-nowrap" style={{ color: "#0f172a" }}>
                      {fmtINR(it.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        ) : (!predItems || predItems.length === 0) && (
          <div className="mt-0.5 text-[10px] italic" style={{ color: "#94a3b8" }}>No obligations in this window</div>
        )}
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-2 text-xs">
      <span style={{ color: "#334155" }}>{label}</span>
      <span className="font-mono tabular-nums font-bold shrink-0" style={{ color: "#0f172a" }}>{fmtINR(value)}</span>
    </div>
  );
}
